// Map markers: a shape filled with a colour, with a short text glyph ('H', 'D') or a small drawn pictogram on it.
// The same drawing feeds the 3D view's sprites and the little marker pictures in the side panel.
//
// A marker style is { color: '#rrggbb', shape, icon }; icon is text (1-3 characters) or 'i:<pictogram>'.

export const SHAPES = [['circle', 'Circle'], ['square', 'Square'], ['diamond', 'Diamond'], ['hex', 'Hexagon'], ['triangle', 'Triangle'], ['pin', 'Pin']];

// pictograms on a 24 x 24 grid: filled paths (even-odd, so holes show the marker colour) and stroked paths
export const PICTOGRAMS = {
  star: { label: 'Star', fill: 'M12 1.8L14.9 8.5 22.2 9.1 16.6 13.9 18.3 21 12 17.2 5.7 21 7.4 13.9 1.8 9.1 9.1 8.5Z' },
  drop: { label: 'Spring / water', fill: 'M12 2C12 2 4.5 10.5 4.5 15.2A7.5 7.5 0 0 0 19.5 15.2C19.5 10.5 12 2 12 2Z' },
  arch: { label: 'Cave entrance', fill: 'M2 21V12.5A10 10 0 0 1 22 12.5V21H17V15.5A5 5 0 0 0 7 15.5V21Z' },
  pick: { label: 'Mine / quarry', stroke: ['M3.5 9.5Q12 1.5 20.5 9.5', 'M12 5.2V22.5'], w: 2.8, rot: -40 },
  house: { label: 'Building / homestead', fill: 'M12 2.5L22 11.2H18.6V21.5H14V15H10V21.5H5.4V11.2H2Z' },
  book: { label: 'Book / source', fill: 'M1.8 5.2Q6.8 2.8 11.2 5.6V20.4Q6.8 17.8 1.8 19.6ZM12.8 5.6Q17.2 2.8 22.2 5.2V19.6Q17.2 17.8 12.8 20.4Z' },
  map: { label: 'Map', fill: 'M2 5.2L8 3 16 5.2 22 3V18.8L16 21 8 18.8 2 21ZM7.3 4.6V18.4H8.7V4.6ZM15.3 6.6V20.4H16.7V6.6Z' },
  flag: { label: 'Flag', fill: 'M4.5 2H6.8V22H4.5ZM6.8 3H19L15.8 7.5 19 12H6.8Z' },
  camera: { label: 'Photo point', fill: 'M2.5 7H7.5L9.3 4.2H14.7L16.5 7H21.5V19.5H2.5ZM12 9A4.3 4.3 0 1 0 12 17.6 4.3 4.3 0 1 0 12 9Z' },
  eye: { label: 'Viewpoint', fill: 'M1 12Q12 1.5 23 12 12 22.5 1 12ZM12 7.8A4.2 4.2 0 1 0 12 16.2 4.2 4.2 0 1 0 12 7.8ZM12 10.2A1.8 1.8 0 1 1 12 13.8 1.8 1.8 0 1 1 12 10.2Z' },
  warning: { label: 'Hazard', fill: 'M12 1.5L23 21.5H1ZM10.8 8.5V15H13.2V8.5ZM10.8 16.5V19H13.2V16.5Z' },
  tent: { label: 'Camp', fill: 'M12 2.5L22.5 21H1.5ZM12 11L8.2 21H15.8Z' },
  tree: { label: 'Tree / landmark', fill: 'M12 1.5L19.5 10.5H15.8L20.5 16.8H13.3V22.5H10.7V16.8H3.5L8.2 10.5H4.5Z' },
  lock: { label: 'Gate / locked', fill: 'M4.5 10.5H19.5V21.5H4.5Z', stroke: ['M7.8 10.5V7.6A4.2 4.2 0 0 1 16.2 7.6V10.5'], w: 2.6 },
};

const path2d = new Map();
const P = (d) => path2d.get(d) || path2d.set(d, new Path2D(d)).get(d);

// dark ink on light colours, white ink on dark ones
function ink(color) {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(color || '');
  if (!m) return '#141413';
  const [r, g, b] = m.slice(1).map(h => parseInt(h, 16) / 255).map(c => (c <= 0.04 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.16 ? '#ffffff' : '#141413';
}

function outline(g, shape) {
  g.beginPath();
  if (shape === 'square') g.roundRect(10, 10, 44, 44, 9);
  else if (shape === 'diamond') { g.moveTo(32, 5); g.lineTo(59, 32); g.lineTo(32, 59); g.lineTo(5, 32); g.closePath(); }
  else if (shape === 'triangle') { g.moveTo(32, 6); g.lineTo(59, 54); g.lineTo(5, 54); g.closePath(); }
  else if (shape === 'hex') { for (let k = 0; k < 6; k++) { const a = Math.PI / 3 * k; g[k ? 'lineTo' : 'moveTo'](32 + 26 * Math.cos(a), 32 + 26 * Math.sin(a)); } g.closePath(); }
  else if (shape === 'pin') { g.arc(32, 24, 19, Math.PI * 0.82, Math.PI * 2.18); g.lineTo(32, 61); g.closePath(); }
  else g.arc(32, 32, 23, 0, Math.PI * 2);
}

// where the glyph sits on each shape: [centre x, centre y, scale]
const SPOT = { triangle: [32, 39, 0.82], pin: [32, 24, 1.05], diamond: [32, 32, 1.02], hex: [32, 32, 1.2], square: [32, 32, 1.2], circle: [32, 32, 1.12] };

export function drawMarker(g, { color, shape = 'circle', icon = '•', ring = false }) {
  outline(g, shape);
  g.fillStyle = color || '#8c8b82'; g.fill();
  g.lineWidth = ring ? 7 : 4; g.strokeStyle = ring ? '#ffffff' : 'rgba(20,20,19,.9)'; g.stroke();
  const [cx, cy, sc] = SPOT[shape] || SPOT.circle, c = ink(color);
  const pic = typeof icon === 'string' && icon.startsWith('i:') ? PICTOGRAMS[icon.slice(2)] : null;
  if (pic) {
    g.save(); g.translate(cx, cy); if (pic.rot) g.rotate(pic.rot * Math.PI / 180);
    const s = sc * 1.18; g.scale(s, s); g.translate(-12, -12);
    g.fillStyle = c; g.strokeStyle = c; g.lineCap = 'round'; g.lineJoin = 'round';
    if (pic.fill) g.fill(P(pic.fill), 'evenodd');
    if (pic.stroke) { g.lineWidth = pic.w || 2.4; for (const d of pic.stroke) g.stroke(P(d)); }
    g.restore();
  } else {
    const t = String(icon ?? '').slice(0, 3), n = [...t].length;
    g.fillStyle = c; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.font = `bold ${Math.round((n <= 1 ? 26 : n === 2 ? 20 : 15) * sc / 1.12)}px system-ui, sans-serif`;
    g.fillText(t, cx, cy + 1);
  }
}

// a 64 x 64 canvas with the marker on it (cached)
const canvases = new Map();
export function markerCanvas(style) {
  const key = `${style.color}|${style.shape}|${style.icon}|${!!style.ring}`;
  if (canvases.has(key)) return canvases.get(key);
  const c = document.createElement('canvas'); c.width = c.height = 64;
  drawMarker(c.getContext('2d'), style);
  canvases.set(key, c); return c;
}

const urls = new Map();
export function markerURL(style) {
  const key = `${style.color}|${style.shape}|${style.icon}`;
  if (!urls.has(key)) urls.set(key, markerCanvas({ ...style, ring: false }).toDataURL());
  return urls.get(key);
}
